/**
 * EngineAuditTable — Shared primitive for rendering engine audit rows.
 *
 * Extracted from EngineAuditPanel rendering patterns as a reusable component.
 * Design law: docs/plans/2026-07-24-k3-black-orange-design-law.md
 *
 * Renders a table of engine results with:
 * - NOT RUN rows for missing engines (dormant StateChip)
 * - VerifiedValue for numeric outputs with provenance and drill-down support
 * - StateChip for verifier_verdict and review_state
 * - Optional renderDetail expand slot
 * - Optional renderActions with blocked context
 * - Optional onDrillRow for drill-down interactions
 */

import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { StateChip } from './StateChip';
import { VerifiedValue } from './VerifiedValue';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/** Non-dollar headline for engines whose output isn't a claim dollar (scores,
 * confidence levels, disqualified caps). Only ever set when `output_value`
 * (the dollar) is null — a dollar headline always wins. */
export interface EngineHeadlineAlt {
  kind: 'score' | 'confidence' | 'disqualified';
  text: string;
  title?: string;
}

export interface EngineRow {
  engine_id: string;
  output_value: number | null;
  method_name: string;
  version: string;
  computed_at: string | null;
  freshness: string;
  support_status: string;
  verifier_verdict: string;
  review_state: string;
  /** Non-dollar headline (score / confidence / disqualified reason). */
  headline_alt?: EngineHeadlineAlt | null;
}

export interface NotRunRow {
  engine_id: string;
  status: 'NOT_RUN';
}

export type EngineAuditTableRow = EngineRow | NotRunRow;

export interface EngineAuditTableProps {
  engines: EngineRow[];
  expectedEngines: string[];
  renderDetail?: (row: EngineAuditTableRow) => React.ReactNode;
  renderActions?: (ctx: {
    row: EngineAuditTableRow;
    blocked: boolean;
  }) => React.ReactNode;
  /** Optional drill-down handler; called when clicking non-NOT_RUN row output cells */
  onDrillRow?: (row: EngineAuditTableRow) => void;
}

// -----------------------------------------------------------------------------
// Helper: Determine if a row is NOT_RUN
// -----------------------------------------------------------------------------

function isNotRunRow(row: EngineAuditTableRow): row is NotRunRow {
  return 'status' in row && row.status === 'NOT_RUN';
}

// -----------------------------------------------------------------------------
// Helper: Map review_state to StateChip state
// -----------------------------------------------------------------------------

// FAIL-CLOSED (K3 review 2026-07-25, conductor-confirmed): on a forensic attestation surface an
// UNKNOWN review state must never render as verified — that is verified-laundering. Only explicit
// approval states earn the hueless verified chip; everything unrecognized demands a human.
function reviewStateToChipState(review_state: string): 'blocker' | 'needs-human' | 'verified' | 'dormant' {
  switch (String(review_state || '').toLowerCase()) {
    case 'blocked':
    case 'disputed':
    case 'rejected':
      return 'blocker';
    case 'waived':
      return 'dormant';
    case 'verified':
    case 'approved':
      return 'verified';
    case 'needs_human':
    case 'needs-human':
    case 'unreviewed':
    default:
      return 'needs-human';
  }
}

// -----------------------------------------------------------------------------
// Helper: Map verifier_verdict to StateChip state
// -----------------------------------------------------------------------------

// FAIL-CLOSED: same rule — an unknown verifier verdict (including 'fail'/'failed') must never
// default to verified. Explicit pass states only; failures are blockers; unknown → needs-human.
function verdictToChipState(verdict: string): 'blocker' | 'warning' | 'verified' | 'needs-human' {
  switch (String(verdict || '').toLowerCase()) {
    case 'blocker':
    case 'contested':
    case 'fail':
    case 'failed':
      return 'blocker';
    case 'warning':
    case 'conditional':
      return 'warning';
    case 'verified':
    case 'supported':
    case 'pass':
    case 'passed':
      return 'verified';
    default:
      return 'needs-human';
  }
}

// -----------------------------------------------------------------------------
// Table Row Component
// -----------------------------------------------------------------------------

interface TableRowProps {
  row: EngineAuditTableRow;
  renderDetail?: (row: EngineAuditTableRow) => React.ReactNode;
  renderActions?: (ctx: { row: EngineAuditTableRow; blocked: boolean }) => React.ReactNode;
  onDrillRow?: (row: EngineAuditTableRow) => void;
}

function TableRow({ row, renderDetail, renderActions, onDrillRow }: TableRowProps) {
  const [expanded, setExpanded] = useState(false);
  const isNotRun = isNotRunRow(row);
  const blocked = !isNotRun && row.review_state === 'blocked';
  // FAIL-CLOSED: only known drillable freshness states allow onDrillRow;
  // unknown freshness → treat as NOT-RUN (needs-human), never drillable
  const DRILLABLE_FRESHNESS = new Set(['fresh', 'stale', 'contested']);
  const freshnessLower = isNotRun ? '' : String(row.freshness || '').toLowerCase();
  const isDrillable = !isNotRun && onDrillRow != null && DRILLABLE_FRESHNESS.has(freshnessLower);

  return (
    <>
      <tr
        className={cn(
          'border-b border-[var(--color-border-faint)]',
          'hover:bg-[var(--color-bg-hover)] transition-colors'
        )}
        data-testid={`engine-row-${row.engine_id}`}
        data-state={isNotRun ? 'not-run' : 'present'}
      >
        {/* Engine ID */}
        <td className="px-4 py-3">
          <span
            className={cn(
              'font-mono text-sm tabular-nums',
              isNotRun
                ? 'text-[var(--color-text-dim)]'
                : 'text-[var(--color-text-primary)]'
            )}
            style={{ fontFamily: 'DM Mono, ui-monospace, monospace' }} // design-law-allow
          >
            {row.engine_id}
          </span>
        </td>

        {/* Output Value */}
        <td className="px-4 py-3 text-right">
          {!isNotRun ? (
            row.output_value != null ? (
              <VerifiedValue
                provenance="E"
                status={freshnessLower as 'fresh' | 'stale' | 'contested' | 'unavailable'}
                value={row.output_value}
                onDrill={isDrillable ? () => onDrillRow?.(row) : undefined}
              />
            ) : row.headline_alt ? (
              row.headline_alt.kind === 'disqualified' ? (
                <span title={row.headline_alt.title}>
                  <StateChip state="dormant" text="Disqualified" size="sm" />
                </span>
              ) : (
                <span className="text-[var(--color-text-muted)]">
                  {row.headline_alt.text}
                </span>
              )
            ) : (
              // Engine ran but produced no headline figure
              <span
                className="text-[var(--color-text-muted)]"
                title="no headline figure"
              >
                —
              </span>
            )
          ) : (
            <span className="text-[var(--color-text-ghost)]">—</span>
          )}
        </td>

        {/* Method + Version */}
        <td className="px-4 py-3">
          {!isNotRun ? (
            <div className="flex flex-col gap-0.5">
              <span
                className="font-mono text-xs text-[var(--color-text-secondary)]"
                style={{ fontFamily: 'DM Mono, ui-monospace, monospace' }} // design-law-allow
              >
                {row.method_name}
              </span>
              <span
                className="font-mono text-xs text-[var(--color-text-muted)]"
                style={{ fontFamily: 'DM Mono, ui-monospace, monospace' }} // design-law-allow
              >
                v{row.version}
              </span>
            </div>
          ) : (
            <span className="text-[var(--color-text-ghost)]">—</span>
          )}
        </td>

        {/* Computed At */}
        <td className="px-4 py-3">
          {!isNotRun && row.computed_at ? (
            <span
              className="font-mono text-xs text-[var(--color-text-muted)]"
              style={{ fontFamily: 'DM Mono, ui-monospace, monospace' }} // design-law-allow
            >
              {new Date(row.computed_at).toLocaleString()}
            </span>
          ) : (
            <span className="text-[var(--color-text-ghost)]">—</span>
          )}
        </td>

        {/* Freshness */}
        <td className="px-4 py-3">
          {!isNotRun ? (
            <StateChip
              state={String(row.freshness || '').toLowerCase() === 'fresh' ? 'verified' : 'warning'}
              text={row.freshness}
              mono
            />
          ) : (
            <span className="text-[var(--color-text-ghost)]">—</span>
          )}
        </td>

        {/* Support Status */}
        <td className="px-4 py-3">
          {!isNotRun ? (
            <StateChip
              state={verdictToChipState(row.support_status)}
              text={row.support_status}
            />
          ) : (
            <span className="text-[var(--color-text-ghost)]">—</span>
          )}
        </td>

        {/* Verifier Verdict */}
        <td className="px-4 py-3">
          {!isNotRun ? (
            <StateChip
              state={verdictToChipState(row.verifier_verdict)}
              text={row.verifier_verdict}
            />
          ) : (
            <span className="text-[var(--color-text-ghost)]">—</span>
          )}
        </td>

        {/* Review State */}
        <td className="px-4 py-3">
          {isNotRun ? (
            <StateChip state="dormant" text="NOT RUN" />
          ) : (
            <StateChip
              state={reviewStateToChipState(row.review_state)}
              text={row.review_state}
            />
          )}
        </td>

        {/* Detail Expand (if renderDetail provided) */}
        {renderDetail && (
          <td className="px-4 py-3">
            <button
              onClick={() => setExpanded(!expanded)}
              className={cn(
                'text-xs font-medium transition-colors',
                expanded
                  ? 'text-[var(--color-brand)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
              )}
              aria-expanded={expanded}
              data-testid={`expand-btn-${row.engine_id}`}
            >
              {expanded ? 'Hide' : 'Details'}
            </button>
          </td>
        )}

        {/* Actions (if renderActions provided) */}
        {renderActions && (
          <td className="px-4 py-3">
            <div className="flex items-center gap-2">
              {renderActions({ row, blocked })}
            </div>
          </td>
        )}
      </tr>

      {/* Expanded Detail Row */}
      {expanded && renderDetail && (
        <tr
          className="bg-[var(--color-bg-elevated)]"
          data-testid={`detail-row-${row.engine_id}`}
        >
          <td
            colSpan={
              8 + (renderDetail ? 1 : 0) + (renderActions ? 1 : 0)
            }
            className="px-4 py-4"
          >
            {renderDetail(row)}
          </td>
        </tr>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

export function EngineAuditTable({
  engines,
  expectedEngines,
  renderDetail,
  renderActions,
  onDrillRow,
}: EngineAuditTableProps) {
  // Build merged rows: all expected engines, with NOT_RUN for missing ones
  const engineMap = new Map(engines.map((e) => [e.engine_id, e]));

  const rows: EngineAuditTableRow[] = expectedEngines.map((engineId) => {
    const engine = engineMap.get(engineId);
    if (engine) {
      return engine;
    }
    return { engine_id: engineId, status: 'NOT_RUN' };
  });

  return (
    <div className="overflow-x-auto">
      <table
        className="w-full border-collapse"
        data-testid="engine-audit-table"
      >
        <thead>
          <tr className="border-b border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)]">
            <th
              className={cn(
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                'text-[var(--color-text-muted)]'
              )}
            >
              Engine
            </th>
            <th
              className={cn(
                'px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider',
                'text-[var(--color-text-muted)]'
              )}
            >
              Output
            </th>
            <th
              className={cn(
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                'text-[var(--color-text-muted)]'
              )}
            >
              Method
            </th>
            <th
              className={cn(
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                'text-[var(--color-text-muted)]'
              )}
            >
              Computed
            </th>
            <th
              className={cn(
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                'text-[var(--color-text-muted)]'
              )}
            >
              Freshness
            </th>
            <th
              className={cn(
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                'text-[var(--color-text-muted)]'
              )}
            >
              Support
            </th>
            <th
              className={cn(
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                'text-[var(--color-text-muted)]'
              )}
            >
              Verdict
            </th>
            <th
              className={cn(
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                'text-[var(--color-text-muted)]'
              )}
            >
              Review
            </th>
            {renderDetail && (
              <th
                className={cn(
                  'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                  'text-[var(--color-text-muted)]'
                )}
              >
                Detail
              </th>
            )}
            {renderActions && (
              <th
                className={cn(
                  'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                  'text-[var(--color-text-muted)]'
                )}
              >
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow
              key={row.engine_id}
              row={row}
              renderDetail={renderDetail}
              renderActions={renderActions}
              onDrillRow={onDrillRow}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default EngineAuditTable;
